import React from 'react';
import { Badge } from 'primereact/badge';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Button } from 'primereact/button';
import { useNotifications } from './NotificationProvider';
import './NotificationBell.css';

export const NotificationBell = () => {
    const op = React.useRef(null);
    const { 
        notifications, 
        unreadCount, 
        clearAll, 
        deleteNotification,
        handleNotificationClick,
        markAsRead 
    } = useNotifications();

    const handleDelete = (e, notificationId) => {
        e.stopPropagation(); // Prevent notification click event
        deleteNotification(notificationId);
    };

    const handleNavigate = (e, notification) => {
        e.stopPropagation(); // Prevent marking as read
        handleNotificationClick(notification);
        op.current.hide(); // Close the overlay panel after navigation
    };

    const handleNotificationBodyClick = (notification) => {
        if (!notification.read) {
            markAsRead(notification.id);
        }
    };

    const formatDate = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);
        const diffInMinutes = Math.floor(diffInSeconds / 60);
        const diffInHours = Math.floor(diffInMinutes / 60);
        const diffInDays = Math.floor(diffInHours / 24);

        if (diffInSeconds < 60) {
            return 'Just now';
        } else if (diffInMinutes < 60) {
            return `${diffInMinutes}m ago`;
        } else if (diffInHours < 24) {
            return `${diffInHours}h ago`;
        } else if (diffInDays === 1) {
            return 'Yesterday';
        } else if (diffInDays < 7) {
            return `${diffInDays}d ago`;
        } else {
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
            });
        }
    };

    return (
        <div className="notification-bell-container">
            <Button 
                icon="pi pi-bell" 
                className="p-button-rounded p-button-text text-light notification-button"
                onClick={(e) => op.current.toggle(e)}
                badge={unreadCount > 0 ? unreadCount.toString() : undefined}
                badgeClassName="p-badge-danger"
                style={{ width: 'fit-content', minWidth: '48px' }}
            />
            
            <OverlayPanel ref={op} className="notification-panel">
                <div className="notification-header">
                    <h3>Notifications</h3>
                    {notifications.length > 0 && (
                        <Button 
                            icon="pi pi-trash" 
                            className="p-button-text p-button-sm"
                            onClick={clearAll}
                            tooltip="Clear all"
                        />
                    )}
                </div>
                <div className="notification-list">
                    {notifications.length === 0 ? (
                        <p className="text-center">No notifications</p>
                    ) : (
                        notifications.map(notification => (
                            <div 
                                key={notification.id} 
                                className={`notification-item ${!notification.read ? 'unread' : ''}`}
                                onClick={() => handleNotificationBodyClick(notification)}
                            >
                                <div className="notification-content">
                                    <div className="notification-header-content">
                                        <h4 className="notification-title">{notification.title}</h4>
                                        {(notification.link || notification.onClick) && (
                                            <Button
                                                icon="pi pi-arrow-right"
                                                className="p-button-rounded p-button-text notification-action-btn"
                                                onClick={(e) => handleNavigate(e, notification)}
                                                tooltip={notification.isExternal ? "Open external link" : "Go to page"}
                                            />
                                        )}
                                    </div>
                                    <p className="notification-message">{notification.message}</p>
                                    <small className="notification-time">
                                        {formatDate(notification.timestamp)}
                                    </small>
                                </div>
                                <Button
                                    icon="pi pi-times"
                                    className="p-button-text p-button-rounded p-button-danger notification-delete-btn"
                                    onClick={(e) => handleDelete(e, notification.id)}
                                    tooltip="Delete"
                                />
                            </div>
                        ))
                    )}
                </div>
            </OverlayPanel>
        </div>
    );
}; 